import { useEffect, useRef, useState, useMemo } from "react"
import { useGlitchEnabled } from "./useGlitchEnabled"
import { useGlitchIntensity } from "./useGlitchIntensity"
import { getGlitchTiming } from "./glitchTiming"
import { GLYPHS, COLOR_BASES, baseChars, singleHit, type GlitchChar, type GlitchColorBase } from "./glitch"

export type { GlitchColorBase }

const DEFAULT_HOT_WORDS = new Set([
  'Nothing', 'Spreadsheets', 'Scattered', 'failures', 'source', 'failed',
  'ERR', 'false', 'undefined', 'router', 'platform', 'route',
  'TRACE', 'GET', 'data', '404', 'NOT_FOUND',
])

const DEFAULT_COLOR_BIAS: Record<string, string> = {
  Nothing: 'text-red-400', failed: 'text-red-300', Spreadsheets: 'text-teal-400',
  Scattered: 'text-teal-400', failures: 'text-red-400', source: 'text-luster-400',
  ERR: 'text-red-400', false: 'text-red-400', undefined: 'text-red-400',
  router: 'text-luster-400', platform: 'text-luster-400', route: 'text-red-400',
  TRACE: 'text-luster-400', GET: 'text-teal-400', data: 'text-violet-400',
  '404': 'text-white', NOT_FOUND: 'text-white/50',
}

export interface FreakoutWordProps {
  word: string
  bold?: boolean
  /** Override global glitch intensity (0–11). Uses global setting if omitted. */
  intensity?: number
  hotWords?: Set<string>
  colorBias?: Record<string, string>
  /** Increment to trigger a burst (GlitchWord-style compat). */
  externalTrigger?: number
  /** Toggle true to trigger a burst (legacy boolean compat). */
  burst?: boolean
  colorBase?: GlitchColorBase
  /** Duration of burst in ms. Default 1500. */
  duration?: number
  /** Suppress ambient glitch — burst still fires. */
  silent?: boolean
  /** Explicit disabled override — bypasses global glitch hook. */
  disabled?: boolean
}

export function FreakoutWord({
  word = "",
  bold,
  intensity,
  hotWords = DEFAULT_HOT_WORDS,
  colorBias = DEFAULT_COLOR_BIAS,
  externalTrigger = 0,
  burst,
  colorBase = 'mixed',
  duration = 1500,
  silent = false,
  disabled,
}: FreakoutWordProps) {
  const [glitchEnabled] = useGlitchEnabled()
  const [globalIntensity] = useGlitchIntensity()

  const isDisabled = disabled !== undefined ? disabled : !glitchEnabled
  const effectiveIntensity = intensity !== undefined ? intensity : globalIntensity
  const timing = getGlitchTiming(isDisabled ? 0 : effectiveIntensity)

  const neutral = useMemo(() => baseChars(word), [word])
  const neutralRef = useRef(neutral)
  neutralRef.current = neutral

  const [chars, setChars] = useState<GlitchChar[]>(neutral)

  // Reset on word change without waiting for effect
  const prevWordRef = useRef(word)
  if (prevWordRef.current !== word) {
    prevWordRef.current = word
    setChars(neutral)
  }

  // Keep mutable values in refs so effects don't re-run on every render
  const timingRef = useRef(timing)
  timingRef.current = timing
  const wordRef = useRef(word)
  wordRef.current = word
  const colorBiasRef = useRef(colorBias)
  colorBiasRef.current = colorBias
  const hotWordsRef = useRef(hotWords)
  hotWordsRef.current = hotWords
  const colorBaseRef = useRef(colorBase)
  colorBaseRef.current = colorBase

  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const holdRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const burstTimersRef = useRef<ReturnType<typeof setTimeout>[]>([])
  const scheduleRef = useRef<(() => void) | null>(null)
  const burstingRef = useRef(false)

  const clearAll = () => {
    if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null }
    if (holdRef.current) { clearTimeout(holdRef.current); holdRef.current = null }
    burstTimersRef.current.forEach(clearTimeout)
    burstTimersRef.current = []
  }

  // ── AMBIENT ──────────────────────────────────────────────────────────────
  useEffect(() => {
    if (timing.disabled) {
      setChars(neutralRef.current)
      return
    }

    const schedule = () => {
      const t = timingRef.current
      if (t.disabled) return
      const w = wordRef.current
      const isHot = hotWordsRef.current.has(w)
      const bias = colorBiasRef.current[w]
      // Hot words fire at half the delay — twice as alive
      const minD = isHot ? t.minDelay * 0.5 : t.minDelay
      const maxD = isHot ? t.maxDelay * 0.5 : t.maxDelay
      const delay = minD + Math.random() * (maxD - minD)

      timerRef.current = setTimeout(() => {
        if (burstingRef.current) { schedule(); return }
        if (!silent && Math.random() < t.probability) {
          const pos = Math.floor(Math.random() * w.length)
          if (w[pos] !== ' ') {
            const cols = COLOR_BASES[colorBaseRef.current] ?? COLOR_BASES.mixed
            const col = (bias && Math.random() < 0.6)
              ? bias
              : cols[Math.floor(Math.random() * cols.length)]
            setChars(prev => prev.map((c, i) =>
              i === pos ? { char: GLYPHS[Math.floor(Math.random() * GLYPHS.length)], col } : c
            ))
            const hold = t.holdMin + Math.random() * (t.holdMax - t.holdMin)
            holdRef.current = setTimeout(() => {
              setChars(neutralRef.current)
              holdRef.current = null
            }, hold)
          }
        }
        schedule()
      }, delay)
    }

    scheduleRef.current = schedule
    schedule()
    return () => clearAll()
  }, [timing.disabled, word, silent]) // eslint-disable-line react-hooks/exhaustive-deps

  // ── BURST via externalTrigger number (GlitchWord compat) ─────────────────
  useEffect(() => {
    if (externalTrigger === 0) return
    clearAll()
    burstingRef.current = true
    setChars(singleHit(word, colorBase, 1.0))

    const restartAmbient = () => {
      burstingRef.current = false
      setChars(neutralRef.current)
      scheduleRef.current?.()
    }

    if (duration < 500) {
      const t = setTimeout(restartAmbient, duration)
      burstTimersRef.current.push(t)
      return () => { clearAll(); burstingRef.current = false; scheduleRef.current?.() }
    }

    const startTime = Date.now()
    const queueNext = () => {
      if (Date.now() - startTime >= duration) { restartAmbient(); return }
      const t = setTimeout(() => {
        setChars(singleHit(wordRef.current, colorBaseRef.current, 0.85 + Math.random() * 0.15))
        queueNext()
      }, 80 + Math.random() * 60)
      burstTimersRef.current.push(t)
    }
    queueNext()
    return () => { clearAll(); burstingRef.current = false; scheduleRef.current?.() }
  }, [externalTrigger]) // eslint-disable-line react-hooks/exhaustive-deps

  // ── BURST via boolean toggle — fire-and-forget on true edge, ignore false ──
  const prevBurstRef = useRef(false)
  useEffect(() => {
    if (timing.disabled || !burst || prevBurstRef.current) return
    prevBurstRef.current = true
    clearAll()
    burstingRef.current = true
    const t0 = Date.now()
    const fire = () => {
      if (Date.now() - t0 >= duration) {
        burstingRef.current = false
        setChars(neutralRef.current)
        scheduleRef.current?.()
        return
      }
      setChars(singleHit(wordRef.current, colorBaseRef.current, silent ? 0.5 : 0.7))
      burstTimersRef.current.push(setTimeout(fire, 60 + Math.random() * 40))
    }
    fire()
    burstTimersRef.current.push(setTimeout(() => {
      clearAll()
      burstingRef.current = false
      setChars(neutralRef.current)
      scheduleRef.current?.()
    }, duration + 50))
    return () => { prevBurstRef.current = false }
  }, [burst, timing.disabled]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => () => clearAll(), []) // eslint-disable-line react-hooks/exhaustive-deps

  if (timing.disabled && externalTrigger === 0 && !burst) {
    return <span className={bold ? 'font-bold' : undefined}>{word}</span>
  }

  return (
    <span className={`relative inline-block pr-[0.05em] ${bold ? 'font-bold' : ''}`} aria-label={word}>
      <span className="invisible select-none" aria-hidden="true">{word}</span>
      <span className="absolute inset-0 overflow-hidden whitespace-nowrap">
        {chars.map((c, i) => (
          <span key={i} className={c.col ?? undefined}>{c.char}</span>
        ))}
      </span>
    </span>
  )
}
